import type { StoredUsers } from '~/gql/users/users'
import { useQuery } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { CompletionBadge } from '~/components/CompletionBadge'
import { SidePanel } from '~/components/SidePanel'
import Spinner from '~/components/Spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_USER_SESSIONS } from '~/gql/sessions/sessions'
import { client } from '~/helpers/apollo'
import { userDisplayName } from '~/helpers/users/display-name'
import SessionStatusBadge from '~/pages/backoffice/admin/sessions/components/SessionStatusBadge'

type IProps = {
  open: boolean
  onCancel: () => void
  user: StoredUsers
}

function UserSessionsModal({ open, onCancel, user }: IProps) {
  const { t } = useTranslation()

  const { data, loading } = useQuery(GQL_USER_SESSIONS, {
    client,
    fetchPolicy: 'cache-and-network',
    skip: !open,
    variables: { userId: user.id },
  })

  const sessions = data?.userSessions ?? []

  return (
    <SidePanel
      open={open}
      onOpenChange={onCancel}
      size="lg"
      title={t('user.sessions.title')}
      description={(
        <>
          {t('user.sessions.subtitle', {
            name: userDisplayName(user),
          })}
        </>
      )}
    >
      {loading && sessions.length === 0
        ? (
            <div className="flex justify-center p-10">
              <Spinner className="size-6" />
            </div>
          )
        : sessions.length === 0
          ? (
              <p className="rounded-md border border-dashed border-border p-8 text-center text-muted-foreground">
                {t('user.sessions.empty')}
              </p>
            )
          : (
              <div className="rounded-md border">
                <Table aria-label={t('user.sessions.title')}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('sessions.name')}</TableHead>
                      <TableHead>{t('common.module')}</TableHead>
                      <TableHead>{t('common.status')}</TableHead>
                      <TableHead>{t('user.sessions.progress')}</TableHead>
                      <TableHead>{t('common.score')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => {
                      const score = s.myTracking?.scoreRaw
                      return (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium">{s.name}</TableCell>
                          <TableCell>{s.module?.title}</TableCell>
                          <TableCell>
                            <SessionStatusBadge status={s.status} />
                          </TableCell>
                          <TableCell>
                            <CompletionBadge status={s.myTracking?.completionStatus} />
                          </TableCell>
                          <TableCell>
                            {score != null
                              ? `${score}${s.myTracking?.scoreMax != null ? ` / ${s.myTracking.scoreMax}` : ''}`
                              : '/'}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
    </SidePanel>
  )
}

export default UserSessionsModal
